import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Box, ButtonBase, Checkbox, Divider, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import type { Column } from "@tanstack/react-table";
import { useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DriverRow, TagDefinition } from "@/types/driver";
import type { DataTableFeatures, DataTableInstance } from "@/ui/dataTableFeatures";
import { zinc } from "@/ui/tokens";

export const COLUMN_LABELS: Record<string, string> = {
  name: "Driver",
  recentMessage: "Most Recent Driver Message",
  group: "Group",
  status: "Driver Availability",
  dutyStatus: "Duty Status",
  location: "Location",
  volume: "Volume",
  country: "Country",
  stateProvince: "State/Province",
};

function resolveColumnLabel(columnId: string, tags: TagDefinition[]): string {
  if (columnId.startsWith("tag:")) {
    const tagId = columnId.slice(4);
    return tags.find((t) => t.id === tagId)?.label ?? columnId;
  }
  return COLUMN_LABELS[columnId] ?? columnId;
}

type ColumnRow = { id: string; label: string; isVisible: boolean; canHide: boolean; isPinned: boolean };

function SortableColumnRow({
  row,
  onTogglePin,
  onToggleVisibility,
}: {
  row: ColumnRow;
  onTogglePin: () => void;
  onToggleVisibility: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  return (
    <Box
      ref={(node: HTMLDivElement | null) => setNodeRef(node)}
      data-sb={TestIds.driverView.columnManagerRow}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: isDragging ? zinc[700] : "transparent",
      }}
    >
      <Box
        component="span"
        data-sb={TestIds.driverView.columnManagerDragHandle}
        {...attributes}
        {...listeners}
        sx={{
          display: "inline-flex",
          cursor: "grab",
          color: zinc[400],
          touchAction: "none",
          "&:active": { cursor: "grabbing" },
          "&:hover": { color: "white" },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      {row.canHide ? (
        <Checkbox sx={{ p: 0 }} size="small" checked={row.isVisible} onChange={onToggleVisibility} />
      ) : (
        <Tooltip title="Always visible — this column can't be hidden">
          <Box component="span" sx={{ display: "inline-flex", width: 18, justifyContent: "center", color: zinc[500] }}>
            <LockOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      )}
      <Box
        sx={{
          flex: 1,
          fontSize: "0.875rem",
          color: row.isVisible ? "white" : zinc[400],
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.label}
      </Box>
      <IconButton
        size="small"
        data-sb={TestIds.driverView.columnManagerPin}
        onClick={onTogglePin}
        aria-label={row.isPinned ? "Unpin column" : "Pin column"}
        sx={{ color: row.isPinned ? "white" : zinc[500], "&:hover": { color: "white" } }}
      >
        {row.isPinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
  );
}

/**
 * Column manager popover: lists every user-managed column (excluding the structural always-pinned ones), split into a
 * draggable "Pinned" section (reorders the pinning override) and a draggable "Columns" section (reorders the center
 * order). Each row toggles visibility and pin state, and a footer button resets to the workspace defaults.
 */
export default function ColumnManager({
  table,
  onColumnPinningChange,
  onResetColumns,
  tags,
}: {
  table: DataTableInstance<DriverRow>;
  onColumnPinningChange: (left: string[]) => void;
  onResetColumns: () => void | Promise<void>;
  tags: TagDefinition[];
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const alwaysPinned = new Set(
    table
      .getAllLeafColumns()
      .filter((c) => c.columnDef.meta?.alwaysPinned)
      .map((c) => c.id),
  );
  const pinnedColumns = table.getStartLeafColumns().filter((c) => !alwaysPinned.has(c.id));
  const centerColumns = table.getCenterLeafColumns();
  const pinnedIds = pinnedColumns.map((c) => c.id);

  const toRow = (col: Column<DataTableFeatures, DriverRow>, isPinned: boolean): ColumnRow => ({
    id: col.id,
    label: resolveColumnLabel(col.id, tags),
    isVisible: col.getIsVisible(),
    canHide: col.getCanHide(),
    isPinned,
  });

  const setPinned = (columnId: string, pin: boolean) => {
    onColumnPinningChange(pin ? [...pinnedIds, columnId] : pinnedIds.filter((id) => id !== columnId));
  };

  const handlePinnedDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = pinnedIds.indexOf(String(active.id));
    const to = pinnedIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onColumnPinningChange(arrayMove(pinnedIds, from, to));
  };

  const handleCenterDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = centerColumns.map((c) => c.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    table.setColumnOrder(arrayMove(ids, from, to));
  };

  const sectionLabelSx = {
    px: 0.5,
    py: 0.5,
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: zinc[400],
    textTransform: "uppercase",
    letterSpacing: 0.5,
  } as const;

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={TestIds.driverView.columnManager}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          fontSize: "1rem",
          bgcolor: open ? zinc[600] : zinc[700],
          color: open ? "white" : zinc[300],
          "&:hover": { bgcolor: zinc[600], color: "white" },
          cursor: "pointer",
        }}
      >
        <ViewColumnIcon sx={{ fontSize: 20 }} />
        <span>Columns</span>
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              bgcolor: zinc[800],
              border: `1px solid ${zinc[600]}`,
              boxShadow: "0 0 0 4px rgba(0,0,0,0.5)",
              borderRadius: 1,
              minWidth: 240,
              maxHeight: "70vh",
              overflowY: "auto",
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          {pinnedColumns.length > 0 && (
            <>
              <Typography sx={sectionLabelSx}>Pinned</Typography>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handlePinnedDragEnd}
              >
                <SortableContext items={pinnedIds} strategy={verticalListSortingStrategy}>
                  {pinnedColumns.map((col) => (
                    <SortableColumnRow
                      key={col.id}
                      row={toRow(col, true)}
                      onTogglePin={() => setPinned(col.id, false)}
                      onToggleVisibility={() => col.toggleVisibility()}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
            </>
          )}
          <Typography sx={sectionLabelSx}>Columns</Typography>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleCenterDragEnd}
          >
            <SortableContext items={centerColumns.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {centerColumns.map((col) => (
                <SortableColumnRow
                  key={col.id}
                  row={toRow(col, false)}
                  onTogglePin={() => setPinned(col.id, true)}
                  onToggleVisibility={() => col.toggleVisibility()}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
          <ButtonBase
            data-sb={TestIds.driverView.columnManagerReset}
            onClick={async () => {
              await onResetColumns();
              setOpen(false);
            }}
            sx={{
              width: "100%",
              justifyContent: "center",
              px: 1,
              py: 0.75,
              borderRadius: 1,
              color: zinc[300],
              fontSize: "0.875rem",
              "&:hover": { bgcolor: zinc[700], color: "white" },
            }}
          >
            Reset to defaults
          </ButtonBase>
        </Box>
      </Popover>
    </>
  );
}
